/**
 * Supabase client factories.
 *
 * Two clients live here on purpose:
 *  • `supabase` — the *therapist* / staff session.
 *  • `supabasePortal` — the *patient portal* session.
 *
 * They use distinct `storageKey`s so a patient logging into the portal
 * on the same device does not overwrite their therapist's session.
 *
 * Both URL + anon key come from `@/lib/env`, which validates them once
 * at app boot via `assertEnv()` in `main.tsx`. That gives us a clear
 * "missing variable" error instead of an opaque crash later on.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { env } from '@/lib/env';
import { isSupportModeActive, SupportModeWriteBlockedError } from '@/lib/supportModeFlag';

// A no-op lock keeps Supabase from reaching for `navigator.locks`,
// which is not available in jsdom / SSR. The original implementation
// already used this same trick.
//
// We deliberately keep the loose `any` here so TypeScript does not pick
// the wrong overload of `LockFunc` (the @supabase typings vary between
// versions and have caused build breaks before).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const noopLock = (_name: string, _timeout: number, fn: () => Promise<any>) => fn();

/**
 * Methoden auf dem PostgrestQueryBuilder, die einen Schreibvorgang
 * starten. RLS würde diese Versuche während eines Support-Modus zwar
 * sowieso ablehnen — aber mit einer kryptischen 403-Meldung. Indem wir
 * den Versuch früh und mit einem klaren Fehler abbrechen, kommen
 * `useMutation`-Konsumenten an eine vernünftige UX-Toast statt einer
 * rohen Supabase-Error-Response.
 *
 * `rpc()` bleibt bewusst zugelassen — Custom-RPCs wie
 * `start_support_session` / `end_support_session` müssen während eines
 * Support-Modus funktionieren, sonst kann man ihn nicht beenden.
 */
const WRITE_METHODS = new Set(['insert', 'update', 'upsert', 'delete']);

function wrapWriteGuard<T extends SupabaseClient<Database>>(client: T): T {
  const originalFrom = client.from.bind(client);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (client as any).from = function (table: string) {
    const builder = originalFrom(table as never);
    return new Proxy(builder, {
      get(target, prop, receiver) {
        const value = Reflect.get(target, prop, receiver);
        if (
          typeof value === 'function' &&
          typeof prop === 'string' &&
          WRITE_METHODS.has(prop)
        ) {
          return (...args: unknown[]) => {
            if (isSupportModeActive()) {
              // Custom-Event als Side-Channel zum SupportModeProvider,
              // der daraufhin eine Toast zeigt. Wir wollen die Toast nicht
              // direkt von hier auslösen, weil `client.ts` sonst auf
              // `sonner` angewiesen wäre — und das ist ein cross-cutting
              // Import, der die Test-Setups verkompliziert.
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('nf-support-blocked', {
                    detail: { method: prop, table },
                  }),
                );
              }
              throw new SupportModeWriteBlockedError(prop, table);
            }
            return (value as (...a: unknown[]) => unknown).apply(target, args);
          };
        }
        return value;
      },
    });
  };
  return client;
}

/** Therapist-side client (default). Import as `supabase`. */
export const supabase = wrapWriteGuard(
  createClient<Database>(env.supabaseUrl, env.supabaseAnonKey, {
    auth: {
      flowType: 'pkce',
      persistSession: true,
      autoRefreshToken: true,
      storageKey: 'nutriflow-auth',
      lock: noopLock,
    },
  }),
);

/**
 * Patient-portal client — separate `storageKey` so a patient login does
 * not overwrite the therapist session on the same device. Bewusst NICHT
 * mit `wrapWriteGuard`: Patienten können nie in einem Support-Modus
 * sein (das ist eine Therapeuten-/Admin-Funktion), und der Banner-State
 * lebt im Therapeuten-Tab.
 */
export const supabasePortal = createClient<Database>(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    flowType: 'pkce',
    persistSession: true,
    autoRefreshToken: true,
    storageKey: 'nutriflow-portal-auth',
    lock: noopLock,
  },
});
